"use client";

import ChevronDown from "@/components/icons/chevrons/chevronDown";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface GalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  allImages: string[];
  selectedImage: string;
}

export const GalleryModal = ({
  isOpen,
  onClose,
  allImages,
  selectedImage,
}: GalleryModalProps) => {
  const modalRef = useRef<HTMLDialogElement>(null);
  // Ref for the scrollable thumbnail container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // State for the currently displayed large image
  const [currentImage, setCurrentImage] = useState(selectedImage);

  // Effect to handle showing/hiding the native <dialog> element
  useEffect(() => {
    const modalElement = modalRef.current;
    if (modalElement) {
      if (isOpen) {
        modalElement.showModal();
        document.body.style.overflow = "hidden";
      } else {
        modalElement.close();
      }
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  // Effect to synchronize internal state (currentImage) with the prop (selectedImage)
  useEffect(() => {
    setCurrentImage(selectedImage);

    //  newly selected image
    const index = allImages.findIndex((image) => image === selectedImage);

    // scroll the container to the newly selected image
    const container = scrollContainerRef.current;

    if (container && index !== -1) {
      //make sure the thumbnails are fully rendered
      setTimeout(() => {
        const thumbnailElement = container.children[index] as HTMLElement;

        if (thumbnailElement) {
          thumbnailElement.scrollIntoView({
            behavior: "instant",
            block: "nearest",
            inline: "center",
          });
        }
      }, 0);
    }
  }, [selectedImage, allImages]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === modalRef.current) {
      onClose();
    }
  };

  // Function to handle internal thumbnail click and instantly scroll the gallery
  const handleThumbnailClick = (image: string, index: number) => {
    setCurrentImage(image);

    const container = scrollContainerRef.current;
    if (container) {
      const thumbnailElement = container.children[index] as HTMLElement;

      if (thumbnailElement) {
        thumbnailElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  };

  return createPortal(
    <dialog
      ref={modalRef}
      className="bg-transparent select-none overflow-x-hidden p-0 w-full h-full max-w-full max-h-full flex flex-col items-center justify-center backdrop:bg-black/80"
      onClick={handleBackdropClick}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white z-10 text-2xl cursor-pointer"
      >
        &times;
      </button>

      <section className="w-full flex justify-evenly">
        <button
        className="cursor-pointer"
          onClick={() => {
            // previous image, needs scrolling as well
            const currentIndex = allImages.indexOf(currentImage);
            const prevIndex =
              (currentIndex - 1 + allImages.length) % allImages.length;
            setCurrentImage(allImages[prevIndex]);

                        // scroll the container to the newly selected image
            const container = scrollContainerRef.current;

            if (container) {
              const thumbnailElement = container.children[prevIndex] as HTMLElement;

              if (thumbnailElement) {
                thumbnailElement.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                  inline: "center",
                });
              }
            }
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="-20 0 24 40" width="24" height="40"><g transform="rotate(45)"><path d="m0 0h5v23h23v5h-28z" opacity=".4"/><path d="m1 1h3v23h23v3h-26z" fill="#fff"/></g></svg>
        </button>
        <div className=" w-[90%] md:w-[70%] max-w-[1200px] aspect-video">
          <figure className="w-full h-full relative">
            <Image
              loading="lazy"
              src={currentImage}
              alt="Focused Image"
              className="object-contain"
              fill
              sizes="(min-width: 1715px) 1200px, (min-width: 768px) 70vw, 90vw"
            />
          </figure>

          {/* if this image changes, play a transition */}
        </div>
        <button
          className="rotate-180 cursor-pointer"
          onClick={() => {
            // next image  needs scrolling as well
            const currentIndex = allImages.indexOf(currentImage);
            const nextIndex = (currentIndex + 1) % allImages.length;
            setCurrentImage(allImages[nextIndex]);

            // scroll the container to the newly selected image
            const container = scrollContainerRef.current;

            if (container) {
              const thumbnailElement = container.children[nextIndex] as HTMLElement;

              if (thumbnailElement) {
                thumbnailElement.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                  inline: "center",
                });
              }
            }
          }}
        >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="-20 0 24 40" width="24" height="40"><g transform="rotate(45)"><path d="m0 0h5v23h23v5h-28z" opacity=".4"/><path d="m1 1h3v23h23v3h-26z" fill="#fff"/></g></svg>

        </button>
      </section>

      {/* all the images displayed here */}
      <div
        ref={scrollContainerRef}
        className="flex flex-row gap-2 overflow-x-scroll no-scrollbar w-full p-4 justify-start snap-mandatory snap-x"
      >
        {allImages?.map((image, index) => (
          <figure
            key={index}
            onClick={() => handleThumbnailClick(image, index)}
            className={`relative aspect-[413/232] h-full w-full max-w-32 snap-center cursor-pointer flex-shrink-0 transition-all duration-200 ${
              image === currentImage
                ? "ring-4 ring-white"
                : "ring-1 ring-transparent hover:ring-2 hover:ring-gray-300"
            }`}
          >
            <Image
              src={image}
              loading="lazy"
              fill
              sizes="128px"
              className="object-cover"
              alt={`Gallery thumbnail ${index + 1}`}
            />
          </figure>
        ))}
      </div>
    </dialog>,
    document.body
  );
};
